import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, urls } from "../../api/api";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import PaginationBar from "../../components/Common/PaginationBar";

const statusLabel = (st) => {
  const map = {
    en_attente: "En attente",
    confirme: "Confirmé",
    annule: "Annulé",
    reporte: "Reporté",
    realise: "Réalisé",
    no_show: "No-show",
  };
  return map[st] || st || "-";
};

export default function Clients() {
  document.title = "Clients";

  // ✅ role check
  const rawUser = localStorage.getItem("user") || localStorage.getItem("authUser");
  let currentUser = {};
  try {
    currentUser = rawUser ? JSON.parse(rawUser) : {};
  } catch {
    currentUser = {};
  }
  const isAdmin = currentUser?.role === "admin";

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // add/edit modal (admin only)
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "" });

  // fiche modal (admin + agent)
  const [ficheOpen, setFicheOpen] = useState(false);
  const [ficheLoading, setFicheLoading] = useState(false);
  const [ficheClient, setFicheClient] = useState(null);
  const [ficheForm, setFicheForm] = useState({ full_name: "", phone: "", email: "" });
  const [history, setHistory] = useState([]);

  // import (admin only)
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(urls.clients);
      setClients(Array.isArray(r.data) ? r.data : []);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh().catch((e) => alert(e?.response?.data?.message || "Erreur chargement clients"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredClients = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c) => {
      const name = (c.full_name || "").toLowerCase();
      const phone = (c.phone || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      return name.includes(s) || phone.includes(s) || email.includes(s);
    });
  }, [clients, q]);

  // pagination data
  const totalItems = filteredClients.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginatedClients = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredClients.slice(start, start + pageSize);
  }, [filteredClients, safePage, pageSize]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  // add/edit (admin only)
  const toggle = () => {
    setIsOpen((v) => !v);
    if (isOpen) {
      setEditingId(null);
      setForm({ full_name: "", phone: "", email: "" });
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ full_name: "", phone: "", email: "" });
    setIsOpen(true);
  };

  const save = async () => {
    if (!form.full_name.trim()) return alert("Nom obligatoire");
    try {
      if (editingId) await api.put(`${urls.clients}/${editingId}`, form);
      else await api.post(urls.clients, form);

      toggle();
      await refresh();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (e?.response?.data?.errors ? Object.values(e.response.data.errors).flat().join("\n") : null) ||
        "Erreur sauvegarde";
      alert(msg);
    }
  };

  // fiche (admin + agent)
  const openFiche = async (id) => {
    setFicheOpen(true);
    setFicheLoading(true);
    setFicheClient(null);
    setHistory([]);
    try {
      const r = await api.get(`${urls.clients}/${id}/history`);
      const c = r.data.client;
      setFicheClient(c);
      setFicheForm({
        full_name: c?.full_name || "",
        phone: c?.phone || "",
        email: c?.email || "",
      });
      setHistory(Array.isArray(r.data.rdvs) ? r.data.rdvs : []);
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur fiche client");
      setFicheOpen(false);
    } finally {
      setFicheLoading(false);
    }
  };

  const closeFiche = () => {
    setFicheOpen(false);
    setFicheClient(null);
    setHistory([]);
    setFicheForm({ full_name: "", phone: "", email: "" });
  };

  const ficheChange = (e) => {
    const { name, value } = e.target;
    setFicheForm((p) => ({ ...p, [name]: value }));
  };

  const ficheSave = async () => {
    if (!isAdmin) return; // ✅ agent read-only
    if (!ficheClient?.id) return;
    if (!ficheForm.full_name.trim()) return alert("Nom obligatoire");

    try {
      await api.put(`${urls.clients}/${ficheClient.id}`, ficheForm);
      await refresh();
      await openFiche(ficheClient.id);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (e?.response?.data?.errors ? Object.values(e.response.data.errors).flat().join("\n") : null) ||
        "Erreur sauvegarde fiche";
      alert(msg);
    }
  };

  const ficheDelete = async () => {
    if (!isAdmin) return; // ✅ agent read-only
    if (!ficheClient?.id) return;
    if (!window.confirm("Supprimer ce client ?")) return;
    try {
      await api.delete(`${urls.clients}/${ficheClient.id}`);
      await refresh();
      closeFiche();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur suppression");
    }
  };

  // export/import (admin only)
  const exportCsv = async () => {
    try {
      const r = await api.get(`${urls.clients}/export`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clients.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur export");
    }
  };

  const triggerImport = () => fileRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("file", file);

    setImporting(true);
    try {
      const r = await api.post(`${urls.clients}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert(`Import terminé ✅\nCreated: ${r.data.created}\nUpdated: ${r.data.updated}\nSkipped: ${r.data.skipped}`);
      await refresh();
      if (ficheClient?.id) await openFiche(ficheClient.id);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (err?.response?.data?.errors ? Object.values(err.response.data.errors).flat().join("\n") : null) ||
        "Erreur import";
      alert(msg);
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <React.Fragment>
      <Row className="mb-2">
        <Col md={6}>
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Recherche: nom, téléphone, email..."
          />
        </Col>

        <Col md={6} className="d-flex justify-content-end gap-2">
          <Button color="secondary" onClick={refresh}>
            Refresh
          </Button>

          {/* ✅ admin only actions */}
          {isAdmin && (
            <>
              <Button color="warning" onClick={triggerImport} disabled={importing}>
                {importing ? "Import..." : "Import (CSV)"}
              </Button>
              <Button color="success" onClick={exportCsv}>
                Export (CSV)
              </Button>
              <Button color="primary" onClick={openAdd}>
                + Ajouter client
              </Button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={handleImportFile} />
            </>
          )}
        </Col>
      </Row>

      <Card>
        <CardBody>
          <div className="table-responsive">
            <Table className="table align-middle table-nowrap">
              <thead>
                <tr>
                  <th>Nom & prénom</th>
                  <th>Téléphone (WhatsApp)</th>
                  <th>Email</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4">Chargement...</td>
                  </tr>
                ) : paginatedClients.length === 0 ? (
                  <tr>
                    <td colSpan="4">Aucun client</td>
                  </tr>
                ) : (
                  paginatedClients.map((c) => (
                    <tr key={c.id}>
                      <td>{c.full_name}</td>
                      <td>
                        {c.phone || "-"}
                        {c.phone ? (
                          <a
                            href={`https://wa.me/${String(c.phone).startsWith("0") ? "212" + c.phone.slice(1) : c.phone}`}
                            target="_blank"
                            rel="noreferrer"
                            className="ms-2"
                            title="WhatsApp"
                            style={{ fontSize: 18 }}
                          >
                            📱
                          </a>
                        ) : null}
                      </td>
                      <td>{c.email || "-"}</td>
                      <td>
                        <Button size="sm" color="primary" onClick={() => openFiche(c.id)}>
                          Fiche
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <PaginationBar
            page={safePage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        </CardBody>
      </Card>

      {/* Fiche modal */}
      <Modal isOpen={ficheOpen} toggle={closeFiche} centered size="lg">
        <ModalHeader toggle={closeFiche}>
          {ficheClient ? `Fiche Client #${ficheClient.id}` : "Fiche Client"}
        </ModalHeader>

        <ModalBody>
          {ficheLoading ? (
            <div>Chargement...</div>
          ) : !ficheClient ? (
            <div>Fiche introuvable</div>
          ) : (
            <>
              <Row className="g-3">
                <Col md={4}>
                  <FormGroup>
                    <Label>Nom & prénom</Label>
                    <Input
                      name="full_name"
                      value={ficheForm.full_name}
                      onChange={ficheChange}
                      disabled={!isAdmin}
                    />
                  </FormGroup>
                </Col>

                <Col md={4}>
                  <FormGroup>
                    <Label>Téléphone (WhatsApp)</Label>
                    <Input
                      name="phone"
                      value={ficheForm.phone}
                      onChange={ficheChange}
                      disabled={!isAdmin}
                    />
                  </FormGroup>
                </Col>

                <Col md={4}>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      name="email"
                      type="email"
                      value={ficheForm.email}
                      onChange={ficheChange}
                      placeholder="ex: client@mail.com"
                      disabled={!isAdmin}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <hr />

              <h6 className="mb-2">Historique des rendez-vous</h6>
              <div className="table-responsive">
                <Table className="table table-bordered align-middle">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Service</th>
                      <th>Agent</th>
                      <th>Statut</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center">
                          Aucun RDV
                        </td>
                      </tr>
                    ) : (
                      history.map((r) => (
                        <tr key={r.id}>
                          <td>{new Date(r.start).toLocaleString()}</td>
                          <td>{r.service?.name || r.service_name || "-"}</td>
                          <td>{r.agent?.name || r.agent_name || "-"}</td>
                          <td>{statusLabel(r.status)}</td>
                          <td>{r.notes || r.note || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {isAdmin && (
            <Button color="danger" onClick={ficheDelete} disabled={!ficheClient}>
              Supprimer
            </Button>
          )}
          <Button color="secondary" onClick={closeFiche}>
            Fermer
          </Button>
          {isAdmin && (
            <Button color="primary" onClick={ficheSave} disabled={!ficheClient}>
              Enregistrer
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Add/Edit modal (admin only) */}
      {isAdmin && (
        <Modal isOpen={isOpen} toggle={toggle} centered>
          <ModalHeader toggle={toggle}>{editingId ? "Modifier Client" : "Ajouter Client"}</ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label>Nom & prénom</Label>
                <Input name="full_name" value={form.full_name} onChange={onChange} />
              </FormGroup>
              <FormGroup>
                <Label>Téléphone (WhatsApp)</Label>
                <Input name="phone" value={form.phone} onChange={onChange} />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input name="email" type="email" value={form.email} onChange={onChange} />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggle}>
              Annuler
            </Button>
            <Button color="primary" onClick={save}>
              Enregistrer
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </React.Fragment>
  );
}
